<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-toolbar dense extended color="primary" dark style="padding-top:50px">
        <v-flex xs12 pa-5>
          <v-toolbar-title slot="extension" class="display-2">
            <v-icon x-large style="padding-bottom:10px">settings</v-icon>
            Ingestion Configurations
          </v-toolbar-title>
          Manage continuous data ingestion configurations.
          <v-btn flat dark :to="{ name: 'ingestionDashboard' }" class="mt-2">
            <v-icon left>arrow_back</v-icon>Back to Dashboard
          </v-btn>
        </v-flex>
      </v-toolbar>
    </v-flex>
    <v-flex xs12 pa-3>
      <v-card>
        <v-card-title>
          <v-spacer></v-spacer>
          <v-btn color="primary" dark @click="openAdd">
            <v-icon left>add</v-icon>Add Configuration
          </v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="configurations" :loading="loading">
          <template slot="items" slot-scope="props">
            <td>{{ props.item.Name }}</td>
            <td>{{ props.item.StationId }}</td>
            <td>{{ dataSourceLabel(props.item.DataSourceType) }}</td>
            <td>
              <v-chip :color="props.item.IsActive ? 'success' : 'default'" small>
                {{ props.item.IsActive ? 'Active' : 'Inactive' }}
              </v-chip>
            </td>
            <td>{{ formatDate(props.item.UpdatedAt) }}</td>
            <td class="text-xs-right">
              <v-btn small color="success" depressed @click="runNow(props.item)" v-if="props.item.DataSourceType !== '6'">Run Now</v-btn>
              <v-btn small color="primary" depressed @click="openSchedule(props.item)" v-if="props.item.DataSourceType !== '6'">Schedule</v-btn>
              <v-btn small color="info" depressed
                :to="{ name: 'ingestionJobs', query: { configId: props.item.Id } }">Jobs</v-btn>
              <v-btn small color="warning" depressed @click="openEdit(props.item)">Edit</v-btn>
              <v-btn small color="error" @click="confirmDelete(props.item)">Delete</v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="600" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ isEdit ? 'Edit' : 'Add' }} Configuration</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-form ref="form" lazy-validation>
            <v-text-field label="Configuration Name" v-model="form.Name" :rules="[rules.required]" required></v-text-field>
            <v-text-field label="Aquarius Station Name" v-model="form.StationId" :rules="[rules.required]" required></v-text-field>
            <v-select label="Data Source Type" v-model="form.DataSourceType"
              :items="dataSourceTypes" item-value="value" item-text="text"
              :disabled="isEdit" :rules="[rules.required]" required></v-select>
            <v-alert type="warning" outline :value="true" class="mb-3">
              Once saved, the configuration type cannot be changed. To use a different type, create a new configuration.
            </v-alert>
            <v-textarea v-if="form.DataSourceType !== '6' && form.DataSourceType !== '7'" label="Detail Settings (JSON)"
              v-model="form.DetailSettings" hint="JSON string with source-specific settings"></v-textarea>
            <template v-else-if="form.DataSourceType === '6'">
              <v-alert type="info" outline :value="true" class="mb-3">
                Station Photos is for UI photo upload and API upload only. "Run Now" and "Schedule" are disabled for this type. Use "FTP Station Photos" for scheduled FTP-based photo ingestion.
              </v-alert>
              <v-text-field label="Directory Name (optional)" v-model="photoSettings.directoryName"
                hint="Azure Blob virtual directory path. Defaults to station name."></v-text-field>
              <v-checkbox label="Should Overwrite Existing Blobs" v-model="photoSettings.shouldOverride"></v-checkbox>
            </template>
            <template v-else>
              <v-text-field label="Directory Name (optional)" v-model="ftpPhotoSettings.directoryName"
                hint="Azure Blob virtual directory path. Defaults to station name."></v-text-field>
              <v-checkbox label="Should Overwrite Existing Blobs" v-model="ftpPhotoSettings.shouldOverride"></v-checkbox>
              <v-text-field label="FTP URL" v-model="ftpPhotoSettings.ftpUrl" :rules="[rules.required]" required></v-text-field>
              <v-text-field label="FTP Port" v-model="ftpPhotoSettings.ftpPort" type="number"></v-text-field>
              <v-text-field label="FTP Username" v-model="ftpPhotoSettings.ftpUsername" :rules="[rules.required]" required></v-text-field>
              <v-text-field label="FTP Password" v-model="ftpPhotoSettings.ftpPassword" type="password" :rules="[rules.required]" required></v-text-field>
              <v-text-field label="Filter Directories" v-model="ftpPhotoSettings.filterDirectories"
                hint="Comma-separated list of directories to filter"></v-text-field>
            </template>
            <v-checkbox label="Active" v-model="form.IsActive"></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" outline @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" dark @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-toolbar dark color="error"><v-toolbar-title>Confirm Delete</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-3">Are you sure you want to delete "{{ deleteTarget ? deleteTarget.Name : '' }}"?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outline @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Schedule Assignment Dialog -->
    <v-dialog v-model="showScheduleDialog" max-width="700" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Schedule Assignment for {{ scheduleTarget ? scheduleTarget.Name : '' }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-layout row>
            <v-flex xs5>
              <v-subheader>Available Schedules</v-subheader>
              <v-list dense>
                <v-list-tile v-for="s in availableSchedules" :key="s.Id" @click="addConfigToSchedule(s)">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ s.Name }}</v-list-tile-title>
                    <v-list-tile-sub-title><code>{{ s.CronExpression }}</code></v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action><v-btn icon><v-icon>add_circle</v-icon></v-btn></v-list-tile-action>
                </v-list-tile>
                <v-list-tile v-if="availableSchedules.length === 0">
                  <v-list-tile-content><v-list-tile-title class="grey--text">No schedules available</v-list-tile-title></v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-flex>
            <v-flex xs2 class="text-xs-center"><v-icon large>arrow_forward</v-icon></v-flex>
            <v-flex xs5>
              <v-subheader>Assigned Schedules</v-subheader>
              <v-list dense>
                <v-list-tile v-for="s in assignedSchedules" :key="s.Id" @click="onRemoveSchedule(s)">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ s.Name }}</v-list-tile-title>
                    <v-list-tile-sub-title><code>{{ s.CronExpression }}</code></v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action><v-btn icon><v-icon>remove_circle</v-icon></v-btn></v-list-tile-action>
                </v-list-tile>
                <v-list-tile v-if="assignedSchedules.length === 0">
                  <v-list-tile-content><v-list-tile-title class="grey--text">No schedules assigned</v-list-tile-title></v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" outline @click="cancelScheduleDialog">Cancel</v-btn>
          <v-btn color="primary" dark @click="saveScheduleAssignments" :loading="savingSchedules">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar :color="snackColor" :timeout="6000" top v-model="snackbar">
      {{ snackMessage }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-layout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const ingestion = createNamespacedHelpers('ingestionManagement')

export default {
  data() {
    return {
      loading: false,
      showDialog: false,
      showDeleteDialog: false,
      showScheduleDialog: false,
      isEdit: false,
      deleteTarget: null,
      scheduleTarget: null,
      assignedSchedules: [],
      initialAssignedIds: [],
      savingSchedules: false,
      form: this.emptyForm(),
      photoSettings: { directoryName: '', shouldOverride: false },
      ftpPhotoSettings: { directoryName: '', shouldOverride: false, ftpUrl: '', ftpPort: '', ftpUsername: '', ftpPassword: '', filterDirectories: '' },
      snackbar: false,
      snackColor: 'success',
      snackMessage: '',
      headers: [
        { text: 'Configuration Name', value: 'Name', align: 'left' },
        { text: 'Aquarius Station Name', value: 'StationId', align: 'left' },
        { text: 'Data Source', value: 'DataSourceType', align: 'left' },
        { text: 'Status', value: 'IsActive', align: 'left' },
        { text: 'Updated', value: 'UpdatedAt', align: 'left' },
        { text: 'Actions', value: null, align: 'right', sortable: false },
      ],
      dataSourceTypes: [
        { value: '1', text: 'VDN REST API' },
        { value: '2', text: 'Optimum' },
        { value: '3', text: 'Sutron Logger' },
        { value: '4', text: 'Hydromet Satellite' },
        { value: '5', text: 'Continuous Data Interface' },
        { value: '6', text: 'Station Photos' },
        { value: '7', text: 'FTP Station Photos' },
      ],
      rules: { required: v => !!v || 'Required' },
    }
  },
  computed: {
    ...ingestion.mapGetters(['configurations', 'schedules']),
    availableSchedules() {
      if (!this.scheduleTarget) return []
      const assignedIds = this.assignedSchedules.map(s => s.Id)
      return this.schedules.filter(s => !assignedIds.includes(s.Id))
    },
  },
  methods: {
    ...ingestion.mapActions([
      'fetchConfigurations', 'createConfiguration', 'updateConfiguration', 'deleteConfiguration',
      'fetchSchedules', 'fetchScheduleConfigs', 'assignConfigToSchedule', 'removeConfigFromSchedule',
      'triggerIngestion',
    ]),
    emptyForm() {
      return { StationId: '', DataSourceType: '1', Name: '', DetailSettings: '', IsActive: true }
    },
    openAdd() {
      this.isEdit = false
      this.form = this.emptyForm()
      this.photoSettings = { directoryName: '', shouldOverride: false }
      this.ftpPhotoSettings = { directoryName: '', shouldOverride: false, ftpUrl: '', ftpPort: '', ftpUsername: '', ftpPassword: '', filterDirectories: '' }
      this.showDialog = true
    },
    openEdit(item) {
      this.isEdit = true
      this.form = JSON.parse(JSON.stringify(item))
      this.photoSettings = { directoryName: '', shouldOverride: false }
      this.ftpPhotoSettings = { directoryName: '', shouldOverride: false, ftpUrl: '', ftpPort: '', ftpUsername: '', ftpPassword: '', filterDirectories: '' }
      if (item.DataSourceType === '6' && item.DetailSettings) {
        try { this.photoSettings = JSON.parse(item.DetailSettings) } catch (e) { }
      } else if (item.DataSourceType === '7' && item.DetailSettings) {
        try { this.ftpPhotoSettings = JSON.parse(item.DetailSettings) } catch (e) { }
      }
      this.showDialog = true
    },
    closeDialog() { this.showDialog = false },
    save() {
      const data = JSON.parse(JSON.stringify(this.form))
      if (data.DataSourceType === '6') {
        data.DetailSettings = JSON.stringify(this.photoSettings)
      } else if (data.DataSourceType === '7') {
        data.DetailSettings = JSON.stringify(this.ftpPhotoSettings)
      }
      const onSuccess = () => {
        this.snackMessage = this.isEdit ? 'Configuration updated.' : 'Configuration created.'
        this.snackColor = 'success'; this.snackbar = true
        this.closeDialog(); this.loadData()
      }
      const onError = (err) => {
        this.snackMessage = err.response ? err.response.data : err.message
        this.snackColor = 'error'; this.snackbar = true
      }
      if (this.isEdit) {
        this.updateConfiguration({ id: data.Id, data, successCb: onSuccess, failedCb: onError })
      } else {
        this.createConfiguration({ data, successCb: onSuccess, failedCb: onError })
      }
    },
    confirmDelete(item) { this.deleteTarget = item; this.showDeleteDialog = true },
    doDelete() {
      this.deleteConfiguration({ id: this.deleteTarget.Id,
        successCb: () => {
          this.snackMessage = 'Configuration deleted.'; this.snackColor = 'success'; this.snackbar = true
          this.showDeleteDialog = false
        },
        failedCb: (err) => {
          this.snackMessage = err.response ? err.response.data : err.message
          this.snackColor = 'error'; this.snackbar = true; this.showDeleteDialog = false
        },
      })
    },
    runNow(item) {
      this.triggerIngestion({ configurationIds: [item.Id],
        successCb: (data) => {
          this.snackMessage = `Triggered config "${item.Name}". Batch: ${data.batchId}, Jobs: ${data.jobRunIds.length}`
          this.snackColor = 'success'; this.snackbar = true
        },
        failedCb: (err) => {
          this.snackMessage = err.response ? (err.response.data || err.response.statusText) : err.message
          this.snackColor = 'error'; this.snackbar = true
        },
      })
    },
    openSchedule(item) {
      this.scheduleTarget = item
      this.assignedSchedules = []
      this.initialAssignedIds = []
      this.fetchScheduleConfigsForConfig(item.Id).then(() => {
        this.initialAssignedIds = this.assignedSchedules.map(s => s.Id)
      })
      this.showScheduleDialog = true
    },
    fetchScheduleConfigsForConfig(configId) {
      return this.fetchSchedules({
        failedCb: (err) => {
          this.snackMessage = 'Failed to load schedules: ' + (err.response ? err.response.data : err.message)
          this.snackColor = 'error'; this.snackbar = true
        },
      }).then(() => {
        const promises = this.schedules.map(s =>
          this.fetchScheduleConfigs({ scheduleId: s.Id,
            successCb: (configs) => {
              if (configs.some(c => c.Id === configId || c.ConfigurationId === configId)) {
                if (!this.assignedSchedules.find(a => a.Id === s.Id)) {
                  this.assignedSchedules.push(s)
                }
              }
            },
          })
        )
        return Promise.all(promises)
      })
    },
    addConfigToSchedule(schedule) {
      if (!this.assignedSchedules.find(s => s.Id === schedule.Id)) {
        this.assignedSchedules.push(schedule)
      }
    },
    onRemoveSchedule(schedule) {
      this.assignedSchedules = this.assignedSchedules.filter(s => s.Id !== schedule.Id)
    },
    cancelScheduleDialog() {
      this.showScheduleDialog = false
    },
    saveScheduleAssignments() {
      this.savingSchedules = true
      const currentIds = this.assignedSchedules.map(s => s.Id)
      const toAdd = this.assignedSchedules.filter(s => !this.initialAssignedIds.includes(s.Id))
      const toRemoveIds = this.initialAssignedIds.filter(id => !currentIds.includes(id))

      const calls = []
      toAdd.forEach(s => {
        calls.push(new Promise((resolve, reject) => {
          this.assignConfigToSchedule({
            scheduleId: s.Id, configurationId: this.scheduleTarget.Id,
            successCb: resolve, failedCb: reject,
          })
        }))
      })
      toRemoveIds.forEach(scheduleId => {
        calls.push(new Promise((resolve, reject) => {
          this.removeConfigFromSchedule({
            scheduleId: scheduleId, configId: this.scheduleTarget.Id,
            successCb: resolve, failedCb: reject,
          })
        }))
      })

      Promise.all(calls).then(() => {
        this.snackMessage = 'Schedule assignments saved.'
        this.snackColor = 'success'; this.snackbar = true
        this.savingSchedules = false
        this.showScheduleDialog = false
      }).catch(err => {
        this.snackMessage = err.response ? err.response.data : err.message
        this.snackColor = 'error'; this.snackbar = true
        this.savingSchedules = false
      })
    },
    dataSourceLabel(val) { return (this.dataSourceTypes.find(x => x.value === val) || {}).text || val },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '' },
    loadData() {
      this.loading = true
      Promise.all([
        this.fetchConfigurations(),
        this.fetchSchedules(),
      ]).finally(() => { this.loading = false })
    },
  },
  mounted() { this.loadData() },
}
</script>
